import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase/client';
import { toSantiagoISO } from '../../lib/utils/dateUtils';
import { getSessionPrice, labelMetodoPago, type MetodoPagoDeclarado } from '../../lib/utils/pricing';
import { EmailService } from '../../services/email.service';
import { CalendarServiceProduction } from '../../services/calendar-production.service';
import PatientRegistration from '../TherapistPatients/PatientRegistration';
import MonthPicker from '../ui/MonthPicker';
import './AppointmentScheduler.css';

interface Patient {
  id: string;
  rut: string;
  nombre: string;
  apellido: string;
  email: string;
  telefono: string;
  tarifa_diferenciada?: number | null;
  direccion?: string | null;
  // Sanción administrativa (migración bloqueo_clinica_paciente_2026_09_02.sql).
  // El trigger server-side rechaza el INSERT igual; esto es sólo para el aviso.
  bloqueado_clinica?: boolean;
}

interface AppointmentData {
  paciente_id: string;
  fecha: string;
  hora: string;
  duracion: number;
  // FUNC-131: familiar deshabilitado — ya no se puede crear (el histórico se sigue mostrando).
  tipo_consulta: 'individual' | 'pareja';
  modalidad: 'presencial' | 'online' | 'domicilio';
  valor_consulta: number;
  notas_terapeuta: string;
}

interface SchedulableTherapist {
  id: string;
  nombre: string;
  apellido: string;
  especialidad: string;
  email: string;
}

interface AppointmentSchedulerProps {
  onAppointmentCreated: (appointment: any) => void;
  onCancel: () => void;
  /** 'staff' (terapeuta/director, agenda para sí mismo) | 'investor' (elige terapeuta y paciente de su cartera) */
  mode?: 'staff' | 'investor';
  therapistId?: string;
  therapistName?: string;
  therapistEmail?: string;
  /** Requerido cuando mode='investor' — id de inversionistas.id del usuario logueado */
  investorId?: string;
  selectedDate?: string;
  selectedTime?: string;
  preSelectedPatient?: Patient;
}

// 09:00–20:00 cada 30 min — mismo rango que el booking público
// (CalendarServiceProduction.DAY_HOURS, último inicio 20:00). Antes cortaba en
// 19:30 y el staff no podía agendar a las 20:00 (FUNC-126).
const TIME_SLOTS: string[] = [];
for (let h = 9; h <= 20; h++) {
  for (const m of [0, 30]) {
    if (h === 20 && m > 0) break;
    TIME_SLOTS.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
  }
}

const formatCLP = (v: number) =>
  v.toLocaleString('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 });

const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const TIPO_LABEL: Record<string, string> = {
  individual: 'Terapia Individual',
  pareja:     'Terapia de Pareja',
};

const TIPO_ICON: Record<AppointmentData['tipo_consulta'], string> = {
  individual: '🧑',
  pareja:     '❤️',
};

// Misma tabla de negocio que el booking público (ExistingPatientBooking.tsx:DURATIONS_BY_TYPE)
const DURATIONS_BY_TIPO: Record<AppointmentData['tipo_consulta'], number[]> = {
  individual: [45, 90],
  pareja:     [50, 90],
};

const AppointmentScheduler: React.FC<AppointmentSchedulerProps> = ({
  onAppointmentCreated,
  onCancel,
  mode = 'staff',
  therapistId,
  therapistName = 'Terapeuta',
  therapistEmail = '',
  investorId,
  selectedDate,
  selectedTime,
  preSelectedPatient,
}) => {
  const isInvestor = mode === 'investor';
  const PATIENT_LIST_LIMIT = 8;
  const [patients, setPatients] = useState<Patient[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(preSelectedPatient ?? null);
  const [showPatientRegistration, setShowPatientRegistration] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [usarTarifaDiferenciada, setUsarTarifaDiferenciada] = useState(false);
  const [valorInput, setValorInput] = useState<string>(String(getSessionPrice({ tipo: 'individual', duracion: 45, modalidad: 'presencial', metodo: 'transferencia' })));
  const [direccionInput, setDireccionInput] = useState(preSelectedPatient?.direccion ?? '');
  const [metodoPagoInput, setMetodoPagoInput] = useState<MetodoPagoDeclarado>('transferencia');
  const [therapists, setTherapists] = useState<SchedulableTherapist[]>([]);
  const [selectedTherapistId, setSelectedTherapistId] = useState('');

  const [appointmentData, setAppointmentData] = useState<AppointmentData>({
    paciente_id: preSelectedPatient?.id ?? '',
    fecha: selectedDate ?? todayStr(),
    hora: selectedTime ?? '10:00',
    duracion: DURATIONS_BY_TIPO.individual[0],
    tipo_consulta: 'individual',
    modalidad: 'presencial',
    valor_consulta: getSessionPrice({ tipo: 'individual', duracion: 45, modalidad: 'presencial', metodo: 'transferencia' }) ?? 0,
    notas_terapeuta: '',
  });

  const effectiveTherapistId = isInvestor ? selectedTherapistId : therapistId;

  const [hourAvailability, setHourAvailability] = useState<Map<string, boolean>>(new Map());
  const [isCheckingHours, setIsCheckingHours] = useState(false);

  // Disponibilidad real de horas para la fecha/terapeuta/modalidad/duración
  // elegidos — mismo servicio que usa el booking público (CalendarServiceProduction),
  // así el <select> deja de mostrar como elegible una hora ya tomada.
  useEffect(() => {
    if (!effectiveTherapistId || !appointmentData.fecha) {
      setHourAvailability(new Map());
      return;
    }

    let cancelled = false;
    setIsCheckingHours(true);

    CalendarServiceProduction.getAvailableSlots(
      effectiveTherapistId,
      appointmentData.fecha,
      true,
      appointmentData.modalidad,
      appointmentData.duracion
    )
      .then(slots => {
        if (cancelled) return;
        setHourAvailability(new Map(slots.map(s => [s.time, s.available])));
      })
      .finally(() => {
        if (!cancelled) setIsCheckingHours(false);
      });

    return () => { cancelled = true; };
  }, [effectiveTherapistId, appointmentData.fecha, appointmentData.modalidad, appointmentData.duracion]);

  // Si la hora elegida deja de estar disponible (cambio de fecha/modalidad/duración
  // revela que ahora choca), obligar a re-elegir en vez de dejar seleccionada una
  // hora ocupada.
  useEffect(() => {
    if (hourAvailability.size === 0) return;
    if (hourAvailability.get(appointmentData.hora) === false) {
      setAppointmentData(prev => ({ ...prev, hora: '' }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hourAvailability]);

  // Auto-precio por tipo + duración + modalidad + método de pago, con la misma
  // matriz que el wizard público y el servidor (lib/utils/pricing.ts). Se recalcula
  // al cambiar cualquiera de esos, salvo que el paciente tenga tarifa diferenciada
  // activa (esa manda). Domicilio: precio fijo, ignora tarifa diferenciada.
  // FUNC-105: incluye la promo online de octubre (depende de la fecha).
  const precioLista = getSessionPrice({
    tipo: appointmentData.tipo_consulta,
    duracion: appointmentData.duracion,
    modalidad: appointmentData.modalidad,
    metodo: metodoPagoInput,
    fechaHora: appointmentData.fecha,
  }) ?? 0;

  useEffect(() => {
    const precio = appointmentData.modalidad === 'domicilio'
      ? precioLista
      : (usarTarifaDiferenciada && selectedPatient?.tarifa_diferenciada != null)
        ? selectedPatient.tarifa_diferenciada
        : precioLista;
    setAppointmentData(prev => ({ ...prev, valor_consulta: precio }));
    setValorInput(String(precio));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appointmentData.tipo_consulta, appointmentData.duracion, appointmentData.modalidad, precioLista, usarTarifaDiferenciada, selectedPatient]);

  // Cargar terapeutas activos de la clínica (solo modo investor, para el selector)
  useEffect(() => {
    if (!isInvestor) return;
    supabase.rpc('get_active_therapists_for_scheduling').then(({ data, error }) => {
      if (!error && data) setTherapists(data as SchedulableTherapist[]);
    });
  }, [isInvestor]);

  // Cargar pacientes: staff ve los suyos (RLS), investor ve solo su cartera vía RPC scoped
  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      try {
        if (isInvestor) {
          const { data, error } = await supabase.rpc('get_investor_patients', {
            p_inversionista_id: investorId,
          });
          setPatients(error || !data ? [] : (data as Patient[]));
          setIsLoading(false);
          return;
        }

        const { data, error } = await supabase
          .from('pacientes')
          .select('id, rut, nombre, apellido, email, telefono, tarifa_diferenciada, direccion, bloqueado_clinica')
          .eq('activo', true)
          .order('apellido');

        let loadedPatients: Patient[];
        if (error) {
          // Columna no existe (tarifa_diferenciada o bloqueado_clinica, según
          // qué migración falte en prod) → fallback al set mínimo garantizado.
          // Sin bloqueado_clinica el aviso no aparece, pero el trigger
          // server-side igual rechaza la cita.
          const { data: fallback } = await supabase
            .from('pacientes')
            .select('id, rut, nombre, apellido, email, telefono, direccion')
            .eq('activo', true)
            .order('apellido');
          loadedPatients = (fallback as Patient[]) || [];
        } else {
          loadedPatients = (data as Patient[]) || [];
        }

        setPatients(loadedPatients);

        // Enriquecer preSelectedPatient con tarifa_diferenciada desde la lista cargada
        // (el precio lo recalcula el efecto de auto-precio, no hace falta acá)
        if (preSelectedPatient) {
          const enriched = loadedPatients.find(p => p.id === preSelectedPatient.id);
          if (enriched) {
            setSelectedPatient(enriched);
            setUsarTarifaDiferenciada(enriched.tarifa_diferenciada != null);
            setDireccionInput(enriched.direccion ?? '');
            setAppointmentData(prev => ({ ...prev, paciente_id: enriched.id }));
          }
        }
      } catch {
        // silencioso — lista queda vacía
      } finally {
        setIsLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInvestor, investorId]);

  const filteredPatients = patients.filter(p => {
    const q = searchTerm.toLowerCase();
    return (
      (p.nombre ?? '').toLowerCase().includes(q) ||
      (p.apellido ?? '').toLowerCase().includes(q) ||
      (p.rut ?? '').includes(searchTerm) ||
      (p.email ?? '').toLowerCase().includes(q)
    );
  });

  const handlePatientSelect = (patient: Patient) => {
    setSelectedPatient(patient);
    setUsarTarifaDiferenciada(patient.tarifa_diferenciada != null);
    setDireccionInput(patient.direccion ?? '');
    setAppointmentData(prev => ({ ...prev, paciente_id: patient.id }));
    setSearchTerm('');
  };

  const handlePatientRegistered = (newPatient: Patient) => {
    setPatients(prev => [newPatient, ...prev]);
    handlePatientSelect(newPatient);
    setShowPatientRegistration(false);
  };

  const handleTarifaToggle = (checked: boolean) => {
    setUsarTarifaDiferenciada(checked);
  };

  const handleInputChange = <K extends keyof AppointmentData>(field: K, value: AppointmentData[K]) => {
    setAppointmentData(prev => ({ ...prev, [field]: value }));
  };

  const handleValorChange = (raw: string) => {
    setValorInput(raw);
    const parsed = parseInt(raw, 10);
    if (!isNaN(parsed) && parsed >= 0) {
      setAppointmentData(prev => ({ ...prev, valor_consulta: parsed }));
      if (selectedPatient?.tarifa_diferenciada != null && parsed !== selectedPatient.tarifa_diferenciada) {
        setUsarTarifaDiferenciada(false);
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    if (!appointmentData.paciente_id || !appointmentData.fecha || !appointmentData.hora) {
      setSubmitError('Completá todos los campos obligatorios.');
      return;
    }

    if (!selectedPatient) {
      setSubmitError('Por favor seleccioná un paciente.');
      return;
    }

    if (selectedPatient.bloqueado_clinica) {
      setSubmitError('Este paciente está bloqueado por la clínica. Contactá al director para desbloquearlo.');
      return;
    }

    if (isInvestor && !selectedTherapistId) {
      setSubmitError('Por favor seleccioná un terapeuta.');
      return;
    }

    if (!isInvestor && appointmentData.modalidad === 'domicilio' && !direccionInput.trim()) {
      setSubmitError('Ingresá la dirección para agendar una sesión a domicilio.');
      return;
    }

    setIsSubmitting(true);

    try {
      const fechaHoraISO = toSantiagoISO(appointmentData.fecha, appointmentData.hora);

      // Crear consulta via RPC (maneja overlap check internamente) — firma propia
      // para investor (create_consultation_investor resuelve identidad server-side
      // y calcula el valor, no acepta monto del cliente)
      const { data: rpcData, error: rpcError } = isInvestor
        ? await supabase.rpc('create_consultation_investor', {
            p_patient_id:    appointmentData.paciente_id,
            p_therapist_id:  selectedTherapistId,
            p_fecha_hora:    fechaHoraISO,
            p_duracion:      appointmentData.duracion,
            p_tipo_consulta: appointmentData.tipo_consulta,
            p_modalidad:     appointmentData.modalidad,
            p_metodo_pago_declarado: metodoPagoInput,
          })
        : await supabase.rpc('create_consultation', {
            patient_id:     appointmentData.paciente_id,
            therapist_id:   therapistId,
            fecha_hora:     fechaHoraISO,
            duracion:       appointmentData.duracion,
            tipo_consulta:  appointmentData.tipo_consulta,
            modalidad:      appointmentData.modalidad,
            // ARQ-012 (2026-09-07): create_consultation es una función única.
            // Este caller corre con sesión de terapeuta/director → el cuerpo
            // matchea `auth.uid()` contra el terapeuta de therapist_id y toma
            // la rama STAFF: honra `valor_consulta` del cliente y escribe la
            // nota + audit. `p_notas_terapeuta` ya no desambigua overloads,
            // pero se sigue mandando porque la rama staff lo usa.
            valor_consulta: appointmentData.valor_consulta,
            p_metodo_pago_declarado: metodoPagoInput,
            // SEC-044: nota inicial va en el mismo INSERT de la RPC (queda
            // auditada en consultas_notas_audit) — nunca un .update() aparte
            // que bypasea update_session_clinical_notes.
            p_notas_terapeuta: appointmentData.notas_terapeuta.trim() || null,
          });

      if (rpcError) {
        const msg = (rpcError.message ?? '').toLowerCase();
        if (msg.includes('bloquead')) {
          setSubmitError('Este paciente está bloqueado por la clínica. Contactá al director para desbloquearlo.');
        } else if (msg.includes('superpone') || msg.includes('overlap') || msg.includes('disponible')) {
          setSubmitError('Ya existe una cita en ese horario. Elegí otro horario.');
        } else if (msg.includes('cartera')) {
          setSubmitError('Ese paciente no pertenece a tu cartera.');
        } else if (msg.includes('autorizado')) {
          setSubmitError('No estás autorizado para agendar esta consulta.');
        } else {
          setSubmitError('No se pudo crear la consulta. Intentá nuevamente.');
        }
        return;
      }

      // FUNC-101: en modo investor la RPC devuelve TABLE(consulta_id, valor_final,
      // meet_link) — la RLS de `consultas` no cubre `inversionista_id`, así que
      // el SELECT de abajo devolvía 0 filas y el correo salía con precio $0 / sin
      // meetLink. Con lo que devuelve la RPC no hace falta releer la consulta.
      const investorRow = isInvestor
        ? ((Array.isArray(rpcData) ? rpcData[0] : rpcData) as
            | { consulta_id: string; valor_final: number | null; meet_link: string | null }
            | undefined)
        : undefined;
      const consultaId: string = isInvestor
        ? (investorRow?.consulta_id ?? '')
        : (rpcData as unknown as string);

      if (!consultaId) {
        setSubmitError('No se pudo crear la consulta. Intentá nuevamente.');
        return;
      }

      // Resolver identidad del terapeuta server-side desde therapist_id. En modo
      // staff no todos los callers pasan therapistName/therapistEmail como props
      // (ej. ClinicalRecords, cuyo tipo TherapistIdentity ni siquiera tiene email)
      // — sin esto el correo al terapeuta sale con `to: ''` (Resend 400 → Edge
      // Function 500) y la copia admin nunca se manda.
      let staffTherapist: { nombre: string | null; apellido: string | null; email: string | null; meet_url: string | null } | null = null;
      if (!isInvestor) {
        const { data: tData } = await supabase
          .from('terapeutas')
          .select('nombre, apellido, email, meet_url')
          .eq('id', therapistId)
          .single();
        staffTherapist = tData ?? null;

        // Adjuntar meet_link para consultas online — create_consultation_investor
        // ya lo hace internamente (server-side), solo hace falta en modo staff
        if (appointmentData.modalidad === 'online' && tData?.meet_url) {
          await supabase
            .from('consultas')
            .update({ meet_link: tData.meet_url })
            .eq('id', consultaId);
        }
      }

      // Persistir dirección real del paciente para sesiones a domicilio (campo
      // oculto en modo investor — minimización de datos, no ejecuta la sesión)
      if (!isInvestor && appointmentData.modalidad === 'domicilio' && direccionInput.trim()) {
        await supabase
          .from('pacientes')
          .update({ direccion: direccionInput.trim() })
          .eq('id', selectedPatient.id);
      }

      // Obtener consulta completa para notificaciones y callback. Sólo modo
      // staff: para investor la RLS de `consultas` devuelve null (FUNC-101) y
      // ya tenemos valor_final + meet_link de la RPC.
      const { data: newAppointment } = isInvestor
        ? { data: null }
        : await supabase
            .from('consultas')
            .select('*, pacientes!inner(nombre, apellido, email, telefono)')
            .eq('id', consultaId)
            .single();

      const chosenTherapist = isInvestor ? therapists.find(t => t.id === selectedTherapistId) : null;
      const staffTherapistName = staffTherapist
        ? `${staffTherapist.nombre ?? ''} ${staffTherapist.apellido ?? ''}`.trim()
        : '';
      const finalTherapistName = chosenTherapist
        ? `${chosenTherapist.nombre} ${chosenTherapist.apellido}`
        : (staffTherapistName || therapistName);
      const finalTherapistEmail = chosenTherapist
        ? chosenTherapist.email
        : (staffTherapist?.email || therapistEmail);
      // El valor real lo calcula el server en modo investor (nunca el del input
      // local). FUNC-101: viene en el TABLE que devuelve create_consultation_investor.
      const finalPrice = isInvestor
        ? (investorRow?.valor_final ?? appointmentData.valor_consulta)
        : appointmentData.valor_consulta;
      // meet_link: staff online → persistido arriba y releído en newAppointment;
      // investor online → lo devuelve la RPC (FUNC-101).
      const finalMeetLink = (isInvestor ? investorRow?.meet_link : newAppointment?.meet_link) || undefined;

      // Notificar a paciente, terapeuta y admin (fire-and-forget, no bloquea el flujo)
      EmailService.sendBookingConfirmation({
        bookingId:      consultaId,
        patientName:    `${selectedPatient.nombre} ${selectedPatient.apellido}`,
        patientEmail:   selectedPatient?.email || '',
        patientPhone:   selectedPatient?.telefono || '',
        therapistName:  finalTherapistName,
        therapistEmail: finalTherapistEmail,
        serviceName:    TIPO_LABEL[appointmentData.tipo_consulta] || 'Consulta',
        therapyType:    appointmentData.tipo_consulta,
        date:           appointmentData.fecha,
        time:           appointmentData.hora,
        duration:       appointmentData.duracion,
        price:          finalPrice,
        modality:       appointmentData.modalidad,
        meetLink:       finalMeetLink,
        metodoPagoDeclarado: metodoPagoInput,
      }).catch((err) => {
        console.error('[AppointmentScheduler] Error enviando notificaciones de booking:', err);
      });

      onAppointmentCreated(newAppointment);
    } catch (err: any) {
      setSubmitError(err.message || 'Error inesperado. Intentá nuevamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (showPatientRegistration) {
    return (
      <PatientRegistration
        onPatientRegistered={handlePatientRegistered}
        onCancel={() => setShowPatientRegistration(false)}
        therapistId={therapistId ?? ''}
      />
    );
  }

  return (
    <div className="appointment-scheduler-modal">
      <div className="modal-backdrop" onClick={onCancel} />
      <div className="scheduler-container">

        <div className="scheduler-header">
          <h2>Nueva Cita</h2>
          <button className="close-btn" onClick={onCancel} aria-label="Cerrar">
            <i className="fas fa-times" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="scheduler-form">

          {/* ── PACIENTE ─────────────────────────────────────────── */}
          <div className="form-section">
            <h3>Paciente</h3>

            {!selectedPatient ? (
              <div className="patient-search">
                <div className="search-input-container">
                  <i className="fas fa-search search-icon" />
                  <input
                    type="text"
                    placeholder="Buscar por nombre, RUT o email…"
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    className="search-input"
                    autoFocus
                  />
                </div>

                {isLoading && (
                  <p className="patient-search-hint">Cargando pacientes…</p>
                )}

                {!isLoading && (
                  <div className="patient-list-panel">
                    <div className="patient-list-header">
                      <span className="patient-list-label">
                        {searchTerm ? 'Resultados' : 'Pacientes'}
                        {!searchTerm && patients.length > 0 && (
                          <span className="patient-list-count"> · {patients.length}</span>
                        )}
                      </span>
                      {!isInvestor && (
                        <button
                          type="button"
                          className="new-patient-link"
                          onClick={() => setShowPatientRegistration(true)}
                        >
                          <i className="fas fa-user-plus" /> Nuevo
                        </button>
                      )}
                    </div>

                    {(searchTerm ? filteredPatients : patients).length > 0 ? (
                      <div className="patient-results">
                        {(searchTerm ? filteredPatients : patients).slice(0, PATIENT_LIST_LIMIT).map(p => (
                          <button
                            type="button"
                            key={p.id}
                            className="patient-result"
                            onClick={() => handlePatientSelect(p)}
                          >
                            <div className="patient-avatar-initials patient-avatar-initials--sm">
                              {p.nombre[0]}{p.apellido[0]}
                            </div>
                            <div className="patient-info">
                              <span className="patient-name">
                                {p.nombre} {p.apellido}
                                {p.bloqueado_clinica && (
                                  <span className="patient-blocked-tag">
                                    <i className="fas fa-ban" aria-hidden="true"></i> Bloqueado
                                  </span>
                                )}
                              </span>
                              <span className="patient-rut">{p.rut}</span>
                            </div>
                            <div className="patient-result-right">
                              {p.tarifa_diferenciada != null && (
                                <span className="tarifa-badge">Tarifa especial</span>
                              )}
                              <span className="patient-email">{p.email}</span>
                            </div>
                          </button>
                        ))}
                      </div>
                    ) : (
                      <div className="patient-empty-state">
                        <p>
                          {searchTerm
                            ? `Sin resultados para "${searchTerm}"`
                            : isInvestor
                              ? 'Todavía no hay pacientes en tu cartera.'
                              : 'Todavía no hay pacientes registrados.'}
                        </p>
                        {!isInvestor && (
                          <button
                            type="button"
                            className="new-patient-btn"
                            onClick={() => setShowPatientRegistration(true)}
                          >
                            <i className="fas fa-user-plus" />
                            Registrar nuevo paciente
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="selected-patient">
                <div className="patient-card">
                  <div className="patient-avatar-initials">
                    {selectedPatient.nombre[0]}{selectedPatient.apellido[0]}
                  </div>
                  <div className="patient-info">
                    <span className="patient-name">{selectedPatient.nombre} {selectedPatient.apellido}</span>
                    <span className="patient-rut">{selectedPatient.rut} · {selectedPatient.email}</span>
                  </div>
                  <button
                    type="button"
                    className="change-patient-btn"
                    onClick={() => { setSelectedPatient(null); setUsarTarifaDiferenciada(false); setDireccionInput(''); }}
                  >
                    Cambiar
                  </button>
                </div>
                {selectedPatient.bloqueado_clinica && (
                  <p className="patient-blocked-banner" role="alert">
                    <i className="fas fa-ban" aria-hidden="true"></i>
                    Este paciente está bloqueado por la clínica. No se le pueden agendar
                    citas hasta que el director lo desbloquee.
                  </p>
                )}
              </div>
            )}
          </div>

          {/* ── DETALLES ──────────────────────────────────────────── */}
          {selectedPatient && (
            <div className="form-section">
              <h3>Detalles</h3>

              <div className="form-group">
                <span className="sched-field-label">Fecha</span>
                <MonthPicker
                  value={appointmentData.fecha}
                  onChange={v => handleInputChange('fecha', v)}
                  ariaLabel="Elegí la fecha de la cita"
                />
              </div>

              <div className="form-group">
                <label htmlFor="sched-hora">
                  Hora{isCheckingHours ? ' (verificando disponibilidad…)' : ''}
                </label>
                <select
                  id="sched-hora"
                  value={appointmentData.hora}
                  onChange={e => handleInputChange('hora', e.target.value)}
                  required
                >
                  <option value="" disabled>Seleccioná una hora</option>
                  {TIME_SLOTS.map(t => {
                    const taken = hourAvailability.get(t) === false;
                    return (
                      <option key={t} value={t} disabled={taken}>
                        {t}{taken ? ' — ocupado' : ''}
                      </option>
                    );
                  })}
                </select>
              </div>

              {isInvestor && (
                <div className="sched-pill-group">
                  <span className="sched-pill-group-label">Terapeuta</span>
                  <div className="sched-therapist-grid">
                    {therapists.map(t => (
                      <button
                        type="button"
                        key={t.id}
                        className={`sched-therapist-card ${selectedTherapistId === t.id ? 'selected' : ''}`}
                        onClick={() => setSelectedTherapistId(t.id)}
                      >
                        <span className="sched-therapist-name">{t.nombre} {t.apellido}</span>
                        <span className="sched-therapist-especialidad">{t.especialidad}</span>
                        {selectedTherapistId === t.id && (
                          <span className="sched-selected-indicator">✓</span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="sched-pill-group">
                <span className="sched-pill-group-label">Tipo de terapia</span>
                <div className="sched-therapy-grid">
                  {(Object.keys(TIPO_LABEL) as AppointmentData['tipo_consulta'][]).map(tipo => (
                    <button
                      type="button"
                      key={tipo}
                      className={`sched-therapy-card ${appointmentData.tipo_consulta === tipo ? 'selected' : ''}`}
                      onClick={() => {
                        setAppointmentData(prev => ({
                          ...prev,
                          tipo_consulta: tipo,
                          duracion: DURATIONS_BY_TIPO[tipo].includes(prev.duracion)
                            ? prev.duracion
                            : DURATIONS_BY_TIPO[tipo][0],
                          // Domicilio solo existe para pareja.
                          modalidad: (tipo !== 'pareja' && prev.modalidad === 'domicilio')
                            ? 'presencial'
                            : prev.modalidad,
                        }));
                      }}
                    >
                      <span className="sched-therapy-icon">{TIPO_ICON[tipo]}</span>
                      <span className="sched-therapy-name">{TIPO_LABEL[tipo].replace('Terapia ', '')}</span>
                      {appointmentData.tipo_consulta === tipo && (
                        <span className="sched-selected-indicator">✓</span>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              <div className="sched-pill-group">
                <span className="sched-pill-group-label">Duración</span>
                {appointmentData.modalidad === 'domicilio' ? (
                  <div className="sched-duration-grid">
                    <div className="sched-duration-card selected">
                      <span className="sched-duration-value">90 min</span>
                      <span className="sched-selected-indicator">✓</span>
                    </div>
                  </div>
                ) : (
                  <div className="sched-duration-grid">
                    {DURATIONS_BY_TIPO[appointmentData.tipo_consulta].map(min => (
                      <button
                        type="button"
                        key={min}
                        className={`sched-duration-card ${appointmentData.duracion === min ? 'selected' : ''}`}
                        onClick={() => handleInputChange('duracion', min)}
                      >
                        <span className="sched-duration-value">{min} min</span>
                        {appointmentData.duracion === min && (
                          <span className="sched-selected-indicator">✓</span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="sched-pill-group">
                <span className="sched-pill-group-label">Modalidad</span>
                <div className={`sched-modality-grid ${appointmentData.tipo_consulta === 'pareja' ? 'sched-modality-grid--three' : ''}`}>
                  <button
                    type="button"
                    className={`sched-modality-card ${appointmentData.modalidad === 'presencial' ? 'selected' : ''}`}
                    onClick={() => handleInputChange('modalidad', 'presencial')}
                  >
                    <span className="sched-modality-icon">🏢</span>
                    <span className="sched-modality-name">Presencial</span>
                    {appointmentData.modalidad === 'presencial' && (
                      <span className="sched-selected-indicator">✓</span>
                    )}
                  </button>
                  <button
                    type="button"
                    className={`sched-modality-card ${appointmentData.modalidad === 'online' ? 'selected' : ''}`}
                    onClick={() => handleInputChange('modalidad', 'online')}
                  >
                    <span className="sched-modality-icon">💻</span>
                    <span className="sched-modality-name">Online</span>
                    {appointmentData.modalidad === 'online' && (
                      <span className="sched-selected-indicator">✓</span>
                    )}
                  </button>
                  {appointmentData.tipo_consulta === 'pareja' && (
                    <button
                      type="button"
                      className={`sched-modality-card ${appointmentData.modalidad === 'domicilio' ? 'selected' : ''}`}
                      onClick={() => setAppointmentData(prev => ({ ...prev, modalidad: 'domicilio', duracion: 90 }))}
                    >
                      <span className="sched-modality-icon">🏠</span>
                      <span className="sched-modality-name">Domicilio</span>
                      {appointmentData.modalidad === 'domicilio' && (
                        <span className="sched-selected-indicator">✓</span>
                      )}
                    </button>
                  )}
                </div>
              </div>

              {!isInvestor && appointmentData.modalidad === 'domicilio' && (
                <div className="form-group">
                  <label htmlFor="sched-direccion">Dirección de la sesión</label>
                  <input
                    type="text"
                    id="sched-direccion"
                    value={direccionInput}
                    onChange={e => setDireccionInput(e.target.value)}
                    placeholder="Calle, número, comuna…"
                    required
                  />
                </div>
              )}

              <div className="form-group">
                <label htmlFor="sched-metodo-pago">Forma de pago declarada</label>
                <select
                  id="sched-metodo-pago"
                  value={metodoPagoInput}
                  onChange={e => setMetodoPagoInput(e.target.value as MetodoPagoDeclarado)}
                >
                  <option value="transferencia">{labelMetodoPago('transferencia')}</option>
                  <option value="tarjeta">{labelMetodoPago('tarjeta')}</option>
                </select>
              </div>

              {!isInvestor && (
                <div className="form-group">
                  <label htmlFor="sched-valor">
                    Valor consulta
                    <span className="label-hint">
                      Estándar: {formatCLP(precioLista)}
                    </span>
                  </label>

                  {selectedPatient.tarifa_diferenciada != null && appointmentData.modalidad !== 'domicilio' && (
                    <label className="tarifa-diferenciada-toggle">
                      <input
                        type="checkbox"
                        checked={usarTarifaDiferenciada}
                        onChange={e => handleTarifaToggle(e.target.checked)}
                      />
                      <span>
                        Usar tarifa especial del paciente
                        <strong> {formatCLP(selectedPatient.tarifa_diferenciada)}</strong>
                      </span>
                    </label>
                  )}

                  <input
                    type="number"
                    id="sched-valor"
                    value={valorInput}
                    onChange={e => handleValorChange(e.target.value)}
                    min={0}
                    step={1}
                    required
                  />
                </div>
              )}

              {!isInvestor && (
                <div className="form-group">
                  <label htmlFor="sched-notas">Notas internas (opcional)</label>
                  <textarea
                    id="sched-notas"
                    value={appointmentData.notas_terapeuta}
                    onChange={e => handleInputChange('notas_terapeuta', e.target.value)}
                    placeholder="Observaciones para esta sesión…"
                    rows={3}
                  />
                </div>
              )}
            </div>
          )}

          {submitError && (
            <div className="form-error-message" role="alert">
              <i className="fas fa-exclamation-triangle" />
              {submitError}
            </div>
          )}

          <div className="form-actions">
            <button
              type="button"
              onClick={onCancel}
              className="cancel-btn"
              disabled={isSubmitting}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="submit-btn"
              disabled={!selectedPatient || selectedPatient.bloqueado_clinica || isSubmitting || (isInvestor && !selectedTherapistId)}
            >
              {isSubmitting ? (
                <>
                  <div className="spinner" />
                  Agendando…
                </>
              ) : (
                <>
                  <i className="fas fa-calendar-plus" />
                  Agendar cita
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AppointmentScheduler;
